import React from 'react';
import { FiWifi, FiTv, FiGlobe, FiRadio } from 'react-icons/fi';
import { IMAGES } from '../constants/images';

const services = [
  {
    icon: FiGlobe,
    title: 'Internet',
    badge: 'Residential',
    color: '#3b82f6',
    image: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=1000&q=80',
    imageAlt: 'Person using laptop for work or streaming, representing internet connectivity.',
    desc: 'As an Authorized Dealer, we help you compare internet plans available at your address, complete your order, and coordinate professional installation with the provider. Our support covers plan selection, enrollment, and account setup — the provider delivers and services the connection.',
    features: ['Any internet option & cable', 'Plan comparison and signup', 'Installation scheduling', 'Upfront quotes'],
  },
  {
    icon: FiTv,
    title: 'CABLE TV',
    badge: 'Residential',
    color: '#8b5cf6',
    image: 'https://images.unsplash.com/photo-1520523839897-b0dc1dfb31e8?w=1000&q=80',
    imageAlt: 'Two brothers enjoying a cartoon in a cozy wooden living room.',
    desc: 'We help you compare TV packages from licensed providers, place your order, and schedule professional installation with the provider. Whether you’re upgrading an existing TV service or setting up a new connection, we make enrollment simple and straightforward.',
    features: ['Popular channel bundles', 'HD and streaming add‑ons', 'Installation scheduling', 'Enrollment & account support'],
  },
  {
    icon: FiWifi,
    title: 'Wi‑Fi',
    badge: 'Residential',
    color: '#10b981',
    image: 'https://images.unsplash.com/photo-1512428559087-560fa5ceab42?w=1000&q=80',
    imageAlt: 'A person working on a laptop beside a cup of coffee, showcasing modern digital life.',
    desc: 'We help you review the Wi‑Fi plans and equipment options offered by licensed providers and coordinate professional installation. Our role is plan selection, enrollment, and account setup — your provider operates the network and handles technical service.',
    features: ['Whole‑home coverage options', 'Mesh systems available', 'Equipment options explained', 'Enrollment support'],
  },
  {
    icon: FiRadio,
    title: 'Wireless',
    badge: 'Residential',
    color: '#f59e0b',
    image: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=1000&q=80',
    imageAlt: 'Hands using a tablet, showcasing wireless digital connectivity and modern technology.',
    desc: 'We help you compare wireless plans from licensed providers and complete enrollment and account setup, so getting connected is straightforward. The provider supplies the network, activation, and ongoing service under its own terms.',
    features: ['Reliable LTE/5G options', 'Flexible data plans', 'Portable hotspots', 'Quick activation'],
  },
];

const Services = () => {
  return (
    <section id="services" style={{
      background: 'linear-gradient(165deg, #0f172a 0%, #1e3a8a 60%, #0f172a 100%)',
      padding: 'clamp(40px, 7vw, 100px) var(--cc-px)',
      fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
      position: 'relative', overflow: 'hidden',
    }}>
      {/* Dot grid */}
      <div aria-hidden style={{
        position: 'absolute', inset: 0,
        backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.06) 1px, transparent 1px)',
        backgroundSize: '28px 28px',
      }} />

      <div className="cc-container" style={{ position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 'clamp(32px, 5vw, 56px)' }}>
          <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#60a5fa', letterSpacing: '0.1em', textTransform: 'uppercase', display: 'block', marginBottom: 12 }}>What We Offer</span>
          <h2 style={{ color: '#fff', fontSize: 'clamp(1.75rem, 4vw, 5rem)', fontWeight: 900, margin: '0 0 16px', letterSpacing: '-0.02em' }}>
            OUR SERVICES
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: 'clamp(0.95rem, 2vw, 1.15rem)', maxWidth: 640, margin: '0 auto', lineHeight: 1.85 }}>
            Reliable high-speed Internet and Cable TV services designed to keep your home or business connected and entertained.
          </p>
        </div>

        {/* Cards - min 260px for xs, max 4 cols on 4K */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(260px, 100%), 1fr))', gap: 'clamp(16px, 3vw, 22px)' }}>
          {services.map((service, i) => {
            const Icon = service.icon;
            return (
              <div key={i} style={{
                background: 'rgba(255,255,255,0.04)', backdropFilter: 'blur(12px)',
                border: '1.5px solid rgba(255,255,255,0.1)',
                borderRadius: 22, overflow: 'hidden',
                transition: 'transform 0.25s, box-shadow 0.25s, border-color 0.25s',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-6px)'; e.currentTarget.style.boxShadow = `0 16px 48px rgba(0,0,0,0.25)`; e.currentTarget.style.borderColor = `${service.color}55`; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.15)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}
              >
                {/* Image with centered heading */}
                <div style={{ position: 'relative', height: 'clamp(160px, 22vw, 200px)', overflow: 'hidden' }}>
                  <img
                    src={service.image}
                    alt={service.imageAlt || service.title}
                    onError={e => { e.currentTarget.src = IMAGES.coverage; }}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.4s' }}
                    onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
                    onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                  />
                  {/* Overlay */}
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(10,20,60,0.85) 0%, transparent 60%)' }} />
                  {/* Centered title/content */}
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 10,
                      textAlign: 'center',
                      padding: '0 18px',
                    }}
                  >
                    <div style={{ width: 52, height: 52, borderRadius: 14, background: service.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 6px 20px rgba(0,0,0,0.25)' }}>
                      <Icon style={{ width: 26, height: 26, color: '#fff' }} />
                    </div>
                    <span style={{ color: '#fff', fontWeight: 900, fontSize: '1.6rem', letterSpacing: '-0.01em', textTransform: 'uppercase' }}>{service.title}</span>
                  </div>
                </div>

                {/* Body */}
                <div style={{ padding: '20px 20px 22px', textAlign: 'center' }}>
                  <p style={{ color: 'rgba(255,255,255,0.92)', fontSize: '1rem', lineHeight: 1.9, margin: 0 }}>{service.desc}</p>
                  {service.features && (
                    <ul style={{ margin: '14px 0 0', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
                      {service.features.map((f, idx) => (
                        <li key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'rgba(255,255,255,0.85)', fontSize: '0.82rem' }}>
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: service.color, flexShrink: 0 }} />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
